import { EXPORT_STATUS } from 'dynamo-daos/build/models/jobs/exportRecord';
// Import the dynamo-daos EXPORT_TYPES for type conversions
import { EXPORT_TYPES as DYNAMO_EXPORT_TYPES } from 'dynamo-daos/build/models/jobs/exportRecord';
import { BVH } from 'three/examples/jsm/loaders/BVHLoader.js';
import { proxy, subscribe } from 'valtio';
import { devtools } from 'valtio/utils';

import { loadBVHFiles, mergeBVHFiles } from '@cartwhl/3d/bvh';
import { APIError } from '@cartwhl/common/api/apiRequest';
import deleteFavorite from '@cartwhl/common/api/deleteFavorite';
import deleteJob from '@cartwhl/common/api/deleteJob';
import deleteProjectApi from '@cartwhl/common/api/deleteProject';
import getExportCost from '@cartwhl/common/api/getExportCost';
import getProject from '@cartwhl/common/api/getProject';
import getProjectExport from '@cartwhl/common/api/getProjectExport';
import getProjectGenerationHistory from '@cartwhl/common/api/getProjectHistory';
import getProjects from '@cartwhl/common/api/getProjects';
import postFavorite from '@cartwhl/common/api/postFavorite';
import postJobsMediaUploadLink from '@cartwhl/common/api/postJobsMediaUploadLink';
import postProject from '@cartwhl/common/api/postProject';
import postProjectExistingMotion from '@cartwhl/common/api/postProjectExistingMotion';
import postProjectExport, {
  ExportProps,
} from '@cartwhl/common/api/postProjectExport';
import postProjectsMotion from '@cartwhl/common/api/postProjectsMotion';
import postProjectsProcessMedia from '@cartwhl/common/api/postProjectsProcessMedia';
import postWsAndFetchJob from '@cartwhl/common/api/postWsAndFetchJob';
import putProject from '@cartwhl/common/api/putProject';
import putProjectCharacter from '@cartwhl/common/api/putProjectCharacter';
import putProjectTimeline from '@cartwhl/common/api/putProjectTimeline';
import { StudioModalName } from '@cartwhl/common/stores/navigationStore';
import {
  AsyncState,
  GenerationHistoryFromAPI,
  GenerationType,
  PaginatedAsyncState,
  Project,
  ProjectFromAPI,
  ProjectType,
  RotationOffsets,
  SceneSettings,
} from '@cartwhl/common/types';
import { getHomepageUrl } from '@cartwhl/common/utils';
import chunkArray from '@cartwhl/common/utils/chunkArray';
import handleGenericErrors from '@cartwhl/common/utils/handleGenericErrors';
import notEmpty from '@cartwhl/common/utils/notEmpty';
import {
  FileUploadState,
  FileUploadStatus,
} from '@cartwhl/ui/feature/FileUploader';
import { TimelineData, TimelineItem } from '@cartwhl/ui/feature/Timeline';

import {
  navigationStore,
  noticesStore,
  posingStore,
  sceneSettingsStore,
  userStore,
  webSocketStore,
} from 'stores';
import { getVideoDuration } from 'utils/getVideoDuration';
import uploadUtil from 'utils/uploadUtil';

import { EXPORT_TYPES } from './animationGenerationStore';
import { DEFAULT_POSE_CHARACTER_ID } from './characterPickerStore';
import { extractBvhURLFromProject } from './posingStore';
import { defaultValues } from './sceneSettingsStore';
import { webSocket } from './webSocketStore';

// #region CONSTANTS
export const LEGACY_PROJECT_ID = 'legacy-project';

const IS_DEV_ENV = process.env.NODE_ENV === 'development';

const LEGACY_PROJECT: ProjectFromAPI = {
  accessLevel: 'EDIT',
  accessorID: LEGACY_PROJECT_ID,
  characters: {},
  createdAt: new Date().toISOString(),
  createdBy: LEGACY_PROJECT_ID,
  hideFromHistory: false,
  lastUpdatedAt: new Date().toISOString(),
  pendingJobs: [],
  PK: LEGACY_PROJECT_ID,
  projectID: LEGACY_PROJECT_ID,
  projectName: 'Legacy project',
  sceneSettings: defaultValues as any as SceneSettings,
  sceneSettingsID: LEGACY_PROJECT_ID,
  SK: LEGACY_PROJECT_ID,
  type: ProjectType.MOTION,
};

const ALL_PROJECTS_LIMIT = 8;
const ALL_PROJECTS_HISTORY_LIMIT = 8;

// #endregion

// #region STORE SETUP

type ExportData = {
  estimatedFinishTime: string;
  exportID?: string;
  path?: string;
  settings?: {
    format: EXPORT_TYPES;
    includeMesh: boolean;
  };
  startedAt: string;
};

// Motion state contains all state dedicated to motion mode (as opposed to pose mode)
type MotionState = {
  timelineData: TimelineData | null;
  combinedBvhFile: BVH | null;
  addExistingMotionSubmitting: boolean; // Sven: Not happy with this but cant think of a better way to do it
  stitching: boolean;
};

type ProjectStoreState = {
  allProjects: PaginatedAsyncState<Project[]>;
  bookmarkedProjects: AsyncState<Project[]>;
  currentProject: AsyncState<Project>;
  currentProjectGenerationHistory: PaginatedAsyncState<GenerationHistoryFromAPI>;
  mediaUpload?: FileUploadState;
  export: AsyncState<ExportData>;
  motion: MotionState;
  meshLoadingStatus: 'initial' | 'pending' | 'ready';
  selectedProjectIDs: string[];
  selectedInstances: Record<
    string,
    {
      projectID: string;
      sectionKey?: string;
    }
  >;
  selectedInstanceKeys: string[];
  lastSelectedInstanceKey: string | null;
  deleteStatus: 'idle' | 'pending';
};

const initialState: ProjectStoreState = {
  allProjects: { status: 'initial', data: undefined },
  bookmarkedProjects: { status: 'initial', data: undefined },
  currentProject: { status: 'initial', data: undefined },
  currentProjectGenerationHistory: { status: 'initial', data: undefined },
  export: { status: 'initial', data: undefined },
  motion: {
    timelineData: null,
    combinedBvhFile: null,
    addExistingMotionSubmitting: false,
    stitching: false,
  },
  meshLoadingStatus: 'initial',
  selectedProjectIDs: [],
  selectedInstances: {},
  selectedInstanceKeys: [],
  lastSelectedInstanceKey: null,
  deleteStatus: 'idle',
};

export const state = proxy<ProjectStoreState>(initialState);

if (process.env.NODE_ENV === 'development') {
  devtools(state, { name: 'Project', enabled: true });
}

const INSTANCE_SECTION_PLACEHOLDER = '__default';

export const makeProjectInstanceKey = (
  projectID: string,
  sectionKey?: string,
) =>
  `${projectID}::${encodeURIComponent(
    sectionKey ?? INSTANCE_SECTION_PLACEHOLDER,
  )}`;

function syncSelectedProjectIDs() {
  const uniqueProjectIDs = new Set<string>();
  Object.values(state.selectedInstances).forEach(({ projectID }) => {
    uniqueProjectIDs.add(projectID);
  });
  state.selectedProjectIDs = Array.from(uniqueProjectIDs);
}

function addInstanceToSelection(projectID: string, sectionKey?: string) {
  const instanceKey = makeProjectInstanceKey(projectID, sectionKey);
  if (state.selectedInstances[instanceKey]) {
    return instanceKey;
  }

  state.selectedInstances[instanceKey] = { projectID, sectionKey };
  state.selectedInstanceKeys = [...state.selectedInstanceKeys, instanceKey];
  syncSelectedProjectIDs();
  state.lastSelectedInstanceKey = instanceKey;
  return instanceKey;
}

function removeInstanceByKey(instanceKey: string) {
  const instance = state.selectedInstances[instanceKey];
  if (!instance) {
    return;
  }

  delete state.selectedInstances[instanceKey];
  state.selectedInstanceKeys = state.selectedInstanceKeys.filter(
    (key) => key !== instanceKey,
  );
  syncSelectedProjectIDs();

  if (state.lastSelectedInstanceKey === instanceKey) {
    state.lastSelectedInstanceKey =
      state.selectedInstanceKeys[state.selectedInstanceKeys.length - 1] ?? null;
  }
}

// #endregion

// #region ACTIONS: PROJECT MANAGER
export async function loadProjects(
  sortType: 'date-created' | 'last-edited' = 'date-created',
) {
  const existingData = state.allProjects.data || {
    items: [] as Project[],
    nextToken: undefined,
  };

  const isFirstLoad = existingData.items.length === 0;

  state.allProjects = {
    status: 'pending',
    data: existingData,
  };

  // On first load, also fetch all bookmarks separately
  if (isFirstLoad) {
    state.bookmarkedProjects = { status: 'pending', data: undefined };
  }

  // When sorting by "last-edited", load all projects at once
  // When sorting by "date-created", use pagination
  if (sortType === 'last-edited') {
    let allProjectsData: Project[] = isFirstLoad
      ? [LEGACY_PROJECT]
      : [...existingData.items];
    let currentToken: string | undefined = isFirstLoad
      ? undefined
      : existingData.nextToken;
    let hasMore = true;

    if (isFirstLoad) {
      state.allProjects = {
        status: 'pending',
        data: { items: [LEGACY_PROJECT], nextToken: undefined },
      };
    }

    // Load bookmarks on first load (do this first so they're available)
    if (isFirstLoad) {
      const { data: bookmarks, error: bookmarksError } = await getProjects(
        undefined,
        undefined,
        true,
      );

      if (bookmarksError) {
        state.bookmarkedProjects = {
          status: 'error',
          error: bookmarksError.message || 'Failed to load bookmarked projects',
          data: undefined,
        };
      } else if (bookmarks) {
        state.bookmarkedProjects = { status: 'ready', data: bookmarks };
      }
    }

    // Load projects in batches and update state after each batch
    while (hasMore) {
      const { data, error, nextToken } = await getProjects(50, currentToken);

      if (error) {
        state.allProjects = {
          status: 'error',
          error: error.message || 'Failed to load projects',
          data: undefined,
        };
        handleGenericErrors(error, { redirectTo: 'studio' });
        return;
      }

      if (data) {
        allProjectsData = [...allProjectsData, ...data];
        currentToken = nextToken;
        hasMore = !!nextToken;

        // Update state after each batch so projects appear incrementally
        state.allProjects = {
          status: hasMore ? 'pending' : 'ready',
          data: { items: allProjectsData, nextToken: undefined },
        };
      } else {
        hasMore = false;
      }
    }

    // Final update to mark as ready
    state.allProjects = {
      status: 'ready',
      data: { items: allProjectsData, nextToken: undefined },
    };
  } else {
    // Normal pagination for date-created sorting
    const { data, error, nextToken } = await getProjects(
      ALL_PROJECTS_LIMIT,
      existingData.nextToken,
    );

    if (isFirstLoad) {
      const { data: bookmarks, error: bookmarksError } = await getProjects(
        undefined, // No limit - fetch all bookmarks
        undefined, // No nextToken - fetch all
        true, // bookmarksOnly
      );

      if (bookmarksError) {
        state.bookmarkedProjects = {
          status: 'error',
          error: bookmarksError.message || 'Failed to load bookmarked projects',
          data: undefined,
        };
      } else if (bookmarks) {
        state.bookmarkedProjects = { status: 'ready', data: bookmarks };
      }
    }

    if (error) {
      state.allProjects = {
        status: 'error',
        error: error.message || 'Failed to load projects',
        data: undefined,
      };
      handleGenericErrors(error, { redirectTo: 'studio' });
    } else if (data) {
      state.allProjects = {
        status: 'ready',
        data: isFirstLoad
          ? { items: [LEGACY_PROJECT, ...data], nextToken }
          : { items: [...existingData.items, ...data], nextToken },
      };
    }
  }
}

export function resetProjects() {
  state.allProjects = { status: 'initial', data: undefined };
}

export function setSelectedProject(
  projectID: string | null,
  sectionKey?: string,
) {
  if (projectID === null) {
    state.selectedProjectIDs = [];
    state.selectedInstances = {};
    state.selectedInstanceKeys = [];
    state.lastSelectedInstanceKey = null;
    return;
  }

  const instanceKey = makeProjectInstanceKey(projectID, sectionKey);
  state.selectedInstances = {
    [instanceKey]: { projectID, sectionKey },
  };
  state.selectedInstanceKeys = [instanceKey];
  syncSelectedProjectIDs();
  state.lastSelectedInstanceKey = instanceKey;
}

export function toggleSelectedProject(projectID: string, sectionKey?: string) {
  const instanceKey = makeProjectInstanceKey(projectID, sectionKey);

  if (state.selectedInstances[instanceKey]) {
    removeInstanceByKey(instanceKey);
  } else {
    addInstanceToSelection(projectID, sectionKey);
  }
}

export function selectProjectRange(
  anchorInstanceKey: string,
  targetProjectID: string,
  allProjects: Array<{ projectID: string; sectionKey?: string }>,
  targetSectionKey?: string,
) {
  const anchorIndex = allProjects.findIndex(
    (project) =>
      makeProjectInstanceKey(project.projectID, project.sectionKey) ===
      anchorInstanceKey,
  );

  const targetInstanceKey = makeProjectInstanceKey(
    targetProjectID,
    targetSectionKey,
  );
  const targetIndex = allProjects.findIndex(
    (project) =>
      makeProjectInstanceKey(project.projectID, project.sectionKey) ===
      targetInstanceKey,
  );

  if (anchorIndex === -1 || targetIndex === -1) {
    setSelectedProject(targetProjectID, targetSectionKey);
    return;
  }

  const minIndex = Math.min(anchorIndex, targetIndex);
  const maxIndex = Math.max(anchorIndex, targetIndex);
  const rangeProjects = allProjects.slice(minIndex, maxIndex + 1);

  rangeProjects.forEach((project) => {
    addInstanceToSelection(project.projectID, project.sectionKey);
  });

  state.lastSelectedInstanceKey = targetInstanceKey;
}

export function clearSelectedProjects() {
  state.selectedProjectIDs = [];
  state.selectedInstances = {};
  state.selectedInstanceKeys = [];
  state.lastSelectedInstanceKey = null;
}

export async function loadBookmarkedProjects() {
  // Keep existing data while loading to prevent thumbnails disappearing and reappearing
  const existingData = state.bookmarkedProjects.data;

  state.bookmarkedProjects = {
    status: 'pending',
    data: existingData,
  };

  const { data: bookmarks, error: bookmarksError } = await getProjects(
    undefined,
    undefined,
    true,
  );

  if (bookmarksError) {
    state.bookmarkedProjects = {
      status: 'error',
      error: bookmarksError.message || 'Failed to load bookmarked projects',
      data: undefined,
    };
  } else if (bookmarks) {
    state.bookmarkedProjects = { status: 'ready', data: bookmarks };
  }
}

export async function createNewProject(type: ProjectType) {
  if (state.currentProject.status === 'pending') {
    IS_DEV_ENV &&
      console.log(
        'currentProject is pending, skipping createNewProject because its already in progress',
      );
    return;
  }
  state.currentProject = { status: 'pending' };
  const sceneSettings = sceneSettingsStore.transformStateToApiShape();

  if (type === ProjectType.POSE) {
    sceneSettings.cameraSettings.aspectRatio = 1;
    sceneSettings.characterSettings.charUploadID = DEFAULT_POSE_CHARACTER_ID;
  }

  const defaultCharacterID = sceneSettings.characterSettings.charUploadID;
  const defaultInstanceID = `${defaultCharacterID}_0`;
  const { data, error } = await postProject({
    projectName: 'Untitled project',
    sceneSettings,
    characters: {
      [defaultInstanceID]: {
        characterID: sceneSettings.characterSettings.charUploadID,
        instanceID: defaultInstanceID,
        timeline: [],
      },
    },
    type: type,
  });

  if (error) {
    handleGenericErrors(error);
  } else if (data) {
    state.currentProject = { status: 'ready', data };

    // Reset the generation history async state
    state.currentProjectGenerationHistory = {
      status: 'initial',
      data: undefined,
    };

    // Add to allProjects list if it's already loaded
    if (state.allProjects.status === 'ready') {
      state.allProjects = {
        status: 'ready',
        data: {
          items: [data, ...state.allProjects.data.items],
          nextToken: state.allProjects.data.nextToken,
        },
      };
    }

    if (type === ProjectType.POSE) {
      navigationStore.navigate(`/pose/${data.projectID}`);
    } else {
      navigationStore.navigate(`/motion/${data.projectID}`);
    }

    return data;
  }
}

export async function deleteProject(projectID: string) {
  if (projectID === LEGACY_PROJECT_ID) {
    noticesStore.info('Your motion project is not deletable.');
    return;
  }

  state.deleteStatus = 'pending';
  const { error } = await deleteProjectApi(projectID);

  if (error) {
    handleGenericErrors(error);
    state.deleteStatus = 'idle';
    return;
  }

  if (state.allProjects.status === 'ready') {
    state.allProjects = {
      status: 'ready',
      data: {
        ...state.allProjects.data,
        items: state.allProjects.data.items.filter(
          (project) => project.projectID !== projectID,
        ),
      },
    };
  }

  if (
    (state.bookmarkedProjects.status === 'ready' ||
      state.bookmarkedProjects.status === 'pending') &&
    state.bookmarkedProjects.data
  ) {
    state.bookmarkedProjects = {
      status: state.bookmarkedProjects.status,
      data: state.bookmarkedProjects.data.filter(
        (project) => project.projectID !== projectID,
      ),
    };
  }

  if (
    state.currentProject.status === 'ready' &&
    state.currentProject.data.projectID === projectID
  ) {
    navigationStore.navigate('/projects');
    resetCurrentProject();
  }
  noticesStore.success('Project deleted!');
  state.deleteStatus = 'idle';
}

export async function deleteSelectedProjects() {
  const projectIDsToDelete = [...state.selectedProjectIDs];

  // Check if legacy project is in selection
  const hasLegacyProject = projectIDsToDelete.includes(LEGACY_PROJECT_ID);

  // Filter out legacy project
  const deletableProjectIDs = projectIDsToDelete.filter(
    (id) => id !== LEGACY_PROJECT_ID,
  );

  if (deletableProjectIDs.length === 0) {
    if (projectIDsToDelete.length > 0) {
      noticesStore.info('The legacy project cannot be deleted.');
    }
    return;
  }

  // Show notice if legacy project was in selection but other projects will be deleted
  if (hasLegacyProject) {
    noticesStore.info('The legacy project cannot be deleted.');
  }

  state.deleteStatus = 'pending';
  try {
    const MAX_CONCURRENT_DELETIONS = 10;
    const deletionBatches = chunkArray(
      deletableProjectIDs,
      MAX_CONCURRENT_DELETIONS,
    );

    let successCount = 0;
    let failureCount = 0;

    for (const batch of deletionBatches) {
      const deletePromises = batch.map((projectID) =>
        deleteProjectApi(projectID),
      );
      const results = await Promise.allSettled(deletePromises);

      results.forEach((result) => {
        if (result.status === 'fulfilled' && !result.value.error) {
          successCount++;
        } else {
          failureCount++;
        }
      });
    }

    if (successCount > 0) {
      if (
        (state.allProjects.status === 'ready' ||
          state.allProjects.status === 'pending') &&
        state.allProjects.data
      ) {
        state.allProjects = {
          status: state.allProjects.status,
          data: {
            ...state.allProjects.data,
            items: state.allProjects.data.items.filter(
              (project) => !deletableProjectIDs.includes(project.projectID),
            ),
          },
        };
      }

      if (
        (state.bookmarkedProjects.status === 'ready' ||
          state.bookmarkedProjects.status === 'pending') &&
        state.bookmarkedProjects.data
      ) {
        state.bookmarkedProjects = {
          status: state.bookmarkedProjects.status,
          data: state.bookmarkedProjects.data.filter(
            (project) => !deletableProjectIDs.includes(project.projectID),
          ),
        };
      }

      if (
        state.currentProject.status === 'ready' &&
        deletableProjectIDs.includes(state.currentProject.data.projectID)
      ) {
        navigationStore.navigate('/projects');
        resetCurrentProject();
      }
    }

    if (failureCount > 0) {
      noticesStore.error('Some projects could not be deleted.');
    }
    if (successCount > 0) {
      if (successCount === 1) {
        noticesStore.success('Project deleted!');
      } else {
        noticesStore.success(`${successCount} projects deleted!`);
      }
    }

    clearSelectedProjects();
  } finally {
    state.deleteStatus = 'idle';
  }
}

export async function updateProjectName(
  projectID: string,
  projectName: string,
) {
  const { data, error } = await putProject(projectID, { projectName });

  IS_DEV_ENV && console.log('data', data);

  if (error) {
    handleGenericErrors(error);
  } else if (data) {
    // Update current project if it matches
    if (
      state.currentProject.status === 'ready' &&
      state.currentProject.data.projectID === projectID
    ) {
      state.currentProject = { status: 'ready', data };
    }

    // Update in allProjects list if it's loaded
    if (state.allProjects.status === 'ready') {
      state.allProjects = {
        status: 'ready',
        data: {
          ...state.allProjects.data,
          items: state.allProjects.data.items.map((project) =>
            project.projectID === projectID ? data : project,
          ),
        },
      };
    }

    // Update in bookmarkedProjects list if it's loaded
    if (
      state.bookmarkedProjects.status === 'ready' &&
      state.bookmarkedProjects.data
    ) {
      state.bookmarkedProjects = {
        status: 'ready',
        data: state.bookmarkedProjects.data.map((project) =>
          project.projectID === projectID ? data : project,
        ),
      };
    }
  }
}

export async function updateProjectPrimaryPose(
  projectID: string,
  poseID: string,
) {
  const { data, error } = await putProject(projectID, { poseID });

  if (error) {
    handleGenericErrors(error);
  } else if (data) {
    // Update current project if it matches
    if (
      state.currentProject.status === 'ready' &&
      state.currentProject.data.projectID === projectID
    ) {
      posingStore.updateReloadKey();
      state.currentProject = { status: 'ready', data };

      const renderInformation =
        posingStore.extractRenderedPoseInformationFromProject(
          data,
          state.currentProjectGenerationHistory.data?.items || [],
        );
      if (renderInformation) {
        posingStore.state.renderedPoseURL = renderInformation.renderedPngURL;
        posingStore.state.lastRenderPrompt =
          renderInformation.renderPrompt || '';
      }
    }

    // Update in allProjects list if it's loaded
    if (state.allProjects.status === 'ready') {
      state.allProjects = {
        status: 'ready',
        data: {
          ...state.allProjects.data,
          items: state.allProjects.data.items.map((project) => {
            return project.projectID === projectID ? data : project;
          }),
        },
      };
    }

    // Update in bookmarkedProjects list if it's loaded
    if (
      state.bookmarkedProjects.status === 'ready' &&
      state.bookmarkedProjects.data
    ) {
      state.bookmarkedProjects = {
        status: 'ready',
        data: state.bookmarkedProjects.data.map((project) =>
          project.projectID === projectID ? data : project,
        ),
      };
    }
  }
}

export function clearExportPath() {
  state.export = { status: 'initial', data: undefined };
}

// #endregion

// #region ACTIONS: CURRENT PROJECT
export async function loadCurrentProject(
  projectID: string,
  reloadPose: boolean = true,
) {
  const existingProject = state.currentProject.data;

  state.currentProject = { status: 'pending', data: existingProject };

  const { data, error } = await getProject(projectID);

  if (error) {
    state.currentProject = {
      status: 'error',
      error: error.message || 'Failed to load project',
      data: undefined,
    };
    handleGenericErrors(error);
  } else if (data) {
    const project = data;
    if (project.type === ProjectType.POSE) {
      if (reloadPose) posingStore.fetchPoseFromProject(project);

      const bvhURL = extractBvhURLFromProject(project);
      posingStore.state.poseRenderInformation = {
        bvhURL: bvhURL || '',
        reloadKey: Date.now(),
      };

      if (bvhURL) {
        posingStore.markPoseAsAdjusted();
      }

      const renderInformation =
        posingStore.extractRenderedPoseInformationFromProject(
          project,
          state.currentProjectGenerationHistory.data?.items || [],
        );
      if (renderInformation) {
        posingStore.state.renderedPoseURL = renderInformation.renderedPngURL;
        posingStore.state.lastRenderPrompt =
          renderInformation.renderPrompt || '';
      }
    } else if (project.type === ProjectType.MOTION && project.sceneSettings) {
      // Apply scene settings for MOTION projects (including character ID)
      sceneSettingsStore.reset(project.sceneSettings);
    }
    state.currentProject = { status: 'ready', data: project };

    // check if there's a pending job and add our web socket to it
    if (project.pendingJobs.length > 0) {
      for (let i = 0; i < project.pendingJobs.length; i++) {
        await addWebSocketToPendingJob(project.pendingJobs[i]);
      }
    }

    return true;
  }
  return false;
}

export async function loadCurrentProjectGenerationHistory(projectID: string) {
  const { data, error } = await getProjectGenerationHistory(
    projectID,
    ALL_PROJECTS_HISTORY_LIMIT,
    state.currentProjectGenerationHistory.data?.nextToken,
  );
  if (error) {
    handleGenericErrors(error);
  } else if (data) {
    state.currentProjectGenerationHistory = {
      status: 'ready',
      data: {
        items: data.generationHistory,
        nextToken: data.nextToken,
      },
    };
  }
}

export async function reloadCurrentProjectGenerationHistory() {
  const projectID = state.currentProject.data?.projectID;
  if (!projectID) return;
  state.currentProjectGenerationHistory = { status: 'pending' };
  await loadCurrentProjectGenerationHistory(projectID);
}

export async function addMotionToCurrentProject({
  prompt,
  numFrames,
  moreVariations = false,
}: {
  prompt: string;
  numFrames: number;
  moreVariations?: boolean;
}) {
  if (state.currentProject.status !== 'ready') return;
  if (!webSocket || !webSocket.connectionID || !webSocket.endpoint) {
    console.error('Websockets are not connected.');
    return;
  }

  const projectID = state.currentProject.data.projectID;
  const characters = state.currentProject.data.characters;
  const objectIds = Object.keys(characters);
  const objectID = objectIds[0]; // We don't support more than one character just yet

  if (!objectID) {
    console.error('No character found in project');
    return;
  }

  const payload = {
    objectID,
    prompt,
    numFrames,
    moreVariations,
    sceneSettings: sceneSettingsStore.transformStateToApiShape(),
    wsConnectionID: webSocket.connectionID,
    wsHttpEndpoint: webSocket.endpoint,
  };

  const { data, error } = await postProjectsMotion(projectID, payload);

  if (error) {
    await handleGenericErrors(error);
    return { success: false, error: error.message };
  } else if (data) {
    state.currentProject.data = data;

    // Reload the current project generation history
    reloadCurrentProjectGenerationHistory();

    return { success: true };
  }
  return { success: false };
}

export async function createOrUpdatePoseObject(imageBase64?: string) {
  const userID = userStore.state.userInfo?.userID;
  let res;
  if (state.currentProject.data) {
    if (userID !== state.currentProject.data.createdBy) {
      // TODO: Sharing system and multi-user. Today we don't let you edit because you don't have permissions
      // In the future, we will let you edit if you have the EDIT access level
      console.log('Not updating the pose because the user is not the author');
      return;
    }
    const jobIDs = getJobIDsFromProjectTimeline(state.currentProject.data);
    if (jobIDs.length > 0) {
      res = await posingStore.updateCurrentProjectPose(
        state.currentProject.data,
        imageBase64 || '',
      );
    } else {
      res = await posingStore.createNewPoseObject(
        state.currentProject.data.projectID,
        undefined,
        imageBase64 || '',
      );

      // Reload the current project to update the currentProject state
      await loadCurrentProject(state.currentProject.data.projectID, false);
    }
  }
  if (res?.success === false) {
    noticesStore.error(res.error || 'Failed to create or update pose object');
  }
}

export function resetCurrentProject() {
  state.currentProject = { status: 'initial', data: undefined };
}

export function setMeshLoadingStatus(status: 'initial' | 'pending' | 'ready') {
  state.meshLoadingStatus = status;
}

export async function beginMediaUpload(
  file: File,
  uploadType: 'video' | 'image',
  mediaID?: string,
) {
  if (!webSocket || !webSocket.connectionID || !webSocket.endpoint) {
    console.error('Websockets are not connected.');
    return;
  }

  const projectID = state.currentProject.data?.projectID;
  if (!projectID) {
    console.error('Project ID is not defined');
    return;
  }

  // for posing v1 we take the first instanceID we can find.
  const characterInstanceIDKeys = Object.keys(
    state.currentProject.data?.characters || {},
  );
  if (characterInstanceIDKeys.length === 0) {
    console.error('Character instance ID is not defined');
    return;
  }
  const objectID = characterInstanceIDKeys[0];

  const filename = file.name;
  const fileExtension = filename.split('.').pop()?.toLowerCase() || '';
  const duration = uploadType === 'video' ? await getVideoDuration(file) : 1;

  state.mediaUpload = {
    uploadID: '',
    status: 'UPLOADING',
    createdAt: new Date().toISOString(),
    errorReasons: undefined,
    fileTransferPercent: 0,
    processingPercentage: 0,
  };
  const { data, error } = await postJobsMediaUploadLink({
    extension: fileExtension,
    jobID: undefined,
    mediaID: mediaID,
    projectID,
    wsConnectionID: webSocket.connectionID,
    wsHttpEndpoint: webSocket.endpoint,
  });

  if (error) {
    state.mediaUpload.status = 'ERROR';
    await handleGenericErrors(error);
  } else if (data) {
    state.mediaUpload.uploadID = data.jobID || '';
    state.mediaUpload.status = 'QUEUED';

    await uploadUtil(state.mediaUpload).upload(data.mediaUploadURL, file);

    if (state.mediaUpload) {
      const { data: pData, error: pError } = await postProjectsProcessMedia(
        projectID,
        {
          extension: fileExtension,
          filename: filename,
          jobID: data.jobID,
          mediaID: mediaID,
          objectID,
          type: uploadType,
          videoDuration: duration,
          wsConnectionID: webSocket.connectionID,
          wsHttpEndpoint: webSocket.endpoint,
        },
      );
      if (pData) {
        state.mediaUpload.estimatedProcessingTime =
          pData.estimatedProcessingTime * 1000;
      } else if (pError) {
        if (pError.message.includes('Insufficient spins available for')) {
          navigationStore.setModal(StudioModalName.READY_FOR_MORE_SPINS);
        } else {
          state.mediaUpload.status = 'ERROR';
          state.mediaUpload.errorReasons = [
            `There was an error trying to initiate the ${uploadType} processing, please try again or reach out for help`,
            `Error: ${pError.message}`,
          ];
        }
      }
    }
  }
}

export async function triggerStitching(objectID: string) {
  if (state.currentProject.status !== 'ready') return;

  const projectID = state.currentProject.data.projectID;
  const items = state.motion.timelineData?.[objectID] || [];

  const timeline = items.map((item) => ({
    jobID: item.jobID,
    title: item.title,
    trimStart: item.trimStart,
    trimEnd: item.trimEnd,
    variationNumber: item.variationNumber,
  }));

  state.motion.stitching = true;

  const { data, error } = await putProjectTimeline(projectID, {
    objectID,
    timeline,
    stitchFromIndex: 0, // TODO
    wsConnectionID: webSocketStore.webSocket.connectionID || '',
    wsHttpEndpoint: webSocketStore.webSocket.endpoint || '',
  });

  if (error) {
    state.motion.stitching = false;
    await loadCurrentProject(projectID);
    handleGenericErrors(error);
    return;
  } else if (data) {
    console.log('stitching successful');
    state.currentProject.data = data;
  }

  state.motion.stitching = false;
}

export function setMotionTimelineData(timelineData: TimelineData | null) {
  state.motion.timelineData = timelineData;
}

export function setTimelineItemData(
  objectID: string,
  jobID: string,
  data: { trimStart?: number | null; trimEnd?: number | null },
) {
  if (!state.motion.timelineData?.[objectID]) return;
  state.motion.timelineData[objectID] = state.motion.timelineData[objectID].map(
    (item) => {
      if (item.jobID === jobID) {
        return { ...item, ...data };
      }
      return item;
    },
  );
}

export async function removeFromTimeline(objectID: string, jobID: string) {
  if (!state.motion.timelineData?.[objectID]) return;

  const newItems = state.motion.timelineData[objectID].filter(
    (item) => item.jobID !== jobID,
  );
  state.motion.timelineData[objectID] = newItems;

  triggerStitching(objectID);
}

export function finishMediaProcessing(status: FileUploadStatus) {
  if (!state.mediaUpload) return;

  state.mediaUpload.status = status;
}

export async function exportCurrentProject({
  format,
  ikFeet,
  ikHands,
  includeMesh,
  keyframeSimplification,
  rotationOffsets,
  turnstileToken,
}: {
  format: EXPORT_TYPES;
  ikFeet: boolean;
  ikHands: boolean;
  includeMesh: boolean;
  keyframeSimplification: 'none' | 'reduce' | 'simplify';
  rotationOffsets?: RotationOffsets;
  turnstileToken?: string;
}) {
  if (state.currentProject.status !== 'ready') return;

  // for posing v1 we take the first instanceID we can find.
  const characterInstanceIDKeys = Object.keys(
    state.currentProject.data?.characters || {},
  );
  if (characterInstanceIDKeys.length === 0) {
    console.error('Character instance ID is not defined');
    return;
  }
  const objectID = characterInstanceIDKeys[0];

  if (!webSocket || !webSocket.connectionID || !webSocket.endpoint) {
    noticesStore.error(
      'Websockets are not connected. Try refreshing your page.',
    );
    return;
  }
  state.export = {
    status: 'pending',
    data: {
      estimatedFinishTime: '',
      exportID: '',
      path: '',
      settings: {
        format,
        includeMesh,
      },
      startedAt: '',
    },
  };

  const props: ExportProps = {
    format,
    frameRate: sceneSettingsStore.state.playbackSettings.frameRate,
    frameStepSize: sceneSettingsStore.state.playbackSettings.frameStepSize,
    handID: sceneSettingsStore.state.characterSettings.staticHands,
    ikFeet: ikFeet,
    ikHands: ikHands,
    includeMesh,
    keyframeSimplification,
    moveInPlace: !sceneSettingsStore.state.characterSettings.enableRootMotion,
    overrideCharacterID:
      sceneSettingsStore.state.characterSettings.charUploadID,
    objectID,
    projectID: state.currentProject.data.projectID,
    rotationOffsets,
    turnstileToken,
    wsConnectionID: webSocket.connectionID,
    wsHttpEndpoint: webSocket.endpoint,
  };
  // Add skinHex if it's not the default value.
  if (sceneSettingsStore.state.characterSettings.skinHex !== 'default') {
    props.skinHex = sceneSettingsStore.state.characterSettings.skinHex;
  }
  if (sceneSettingsStore.state.characterSettings.faceExpression) {
    props.faceExpression =
      sceneSettingsStore.state.characterSettings.faceExpression;
  }
  // Trimming a project export should be unnecessary, we already have that information
  // at the project level.

  // if (
  //   state.currentProject.data.type === ProjectType.MOTION &&
  //   (animationPlayerStore.state.trimFrameStart !== undefined ||
  //     animationPlayerStore.state.trimFrameEnd !== undefined)
  // ) {
  //   const baseTrimStart = animationPlayerStore.state.trimFrameStart ?? 0;
  //   const baseTrimEnd =
  //     animationPlayerStore.state.trimFrameEnd ??
  //     animationPlayerStore.state.duration.originalFrames;
  //   const baseFpsRate = Math.round(
  //     animationPlayerStore.state.duration.originalFrames /
  //       animationPlayerStore.state.duration.originalSeconds,
  //   );
  //   const fpsAdjustFactor = props.frameRate / baseFpsRate;

  //   const baseTrimFPSFactored = Math.floor(baseTrimStart * fpsAdjustFactor);
  //   const baseTrimEndFPSFactored = Math.floor(baseTrimEnd * fpsAdjustFactor);
  //   props.trimStart = baseTrimFPSFactored;
  //   props.trimEnd = baseTrimEndFPSFactored;
  // }

  const { data, error } = await postProjectExport(props);

  if (data) {
    state.export = {
      status: 'pending',
      data: {
        ...state.export.data!,
        estimatedFinishTime: data.estimatedFinishTime,
        exportID: data.exportID,
        startedAt: data.exportStartedAt,
      },
    };
  } else if (error) {
    await handleGenericErrors(error);
    state.export = { status: 'error', data: undefined, error: error.message };

    // if error includes Please verify your email first before downloading motions
    if (
      error.message.includes(
        'Please verify your email first before downloading motions',
      )
    ) {
      // redirect them to the profile page.
      // handleGenericErrors will handle showing them a message
      setTimeout(() => {
        window.location.href = getHomepageUrl('/profile/account-details');
      }, 2500);
    }
  }
}

export async function pollProjectExportStatus(
  enableAutoDownload: boolean = false,
) {
  if (
    state.currentProject.status === 'initial' ||
    !state.currentProject.data?.projectID
  )
    return;

  const projectID = state.currentProject.data.projectID;
  const exportID = state.export.data?.exportID;

  try {
    // Fetch the latest job data to check the export status
    const { data, error } = await getProjectExport(projectID, exportID);

    if (error) {
      console.error('Error polling for project export status:', error);
      return;
    }

    if (!data) {
      console.warn(`Export for project ${projectID} not found while polling.`);
      return;
    }

    switch (data.status) {
      case EXPORT_STATUS.QUEUED:
      case EXPORT_STATUS.GENERATING:
        state.export = {
          status: 'pending',
          data: {
            ...state.export.data,
            estimatedFinishTime: data.estimatedFinishTime,
            exportID: data.exportID,
            path: '',
            startedAt: data.startedAt,
          },
        };
        break;
      case EXPORT_STATUS.COMPLETED: {
        if (enableAutoDownload) {
          state.export = {
            status: 'ready',
            data: {
              ...state.export.data,
              path: data.downloadURL,
              estimatedFinishTime: new Date().toISOString(),
              startedAt: data.startedAt,
            },
          };
          userStore.refetchUserInfo();
        } else if (state.export.status === 'pending') {
          state.export = {
            status: 'ready',
            data: {
              ...state.export.data,
              estimatedFinishTime: new Date().toISOString(),
              startedAt: data.startedAt,
            },
          };
        }
        break;
      }
      case EXPORT_STATUS.FAILED:
      case EXPORT_STATUS.REJECTED:
        await handleGenericErrors(
          new APIError('Download failed. Please try again.', 500),
        );
        state.export = {
          status: 'error',
          data: undefined,
          error: 'Download failed. Please try again.',
        };
        break;
      default:
        console.error('Invalid export status: ', data.status);
    }
  } catch (error) {
    console.error('Error in getting export job: ', error);
  }
}

export async function addExistingMotionToProject({
  createAsNewProject,
  existingJobID,
  existingVariationNumber,
  trimStart,
  trimEnd,
  sequenceNumber,
}: {
  createAsNewProject: boolean;
  existingJobID: string;
  existingVariationNumber: number;
  trimStart?: number;
  trimEnd?: number;
  sequenceNumber?: number;
}) {
  if (!webSocket || !webSocket.connectionID || !webSocket.endpoint) {
    console.error('Websockets are not connected.');
    return;
  }

  state.motion.addExistingMotionSubmitting = true;

  try {
    let projectID: string;
    let characters: Project['characters'];

    if (createAsNewProject) {
      const newProject = await createNewProject(ProjectType.MOTION);
      if (!newProject) {
        state.motion.addExistingMotionSubmitting = false;
        return;
      }
      projectID = newProject.projectID;
      characters = newProject.characters;
    } else {
      if (state.currentProject.status !== 'ready') {
        state.motion.addExistingMotionSubmitting = false;
        return;
      }
      projectID = state.currentProject.data.projectID;
      characters = state.currentProject.data.characters;
    }

    const objectIds = Object.keys(characters);
    const objectID = objectIds[0]; // We don't support more than one character just yet

    const payload = {
      objectID,
      existingJobID,
      existingVariationNumber,
      trimStart,
      trimEnd,
      sequenceNumber,
      wsConnectionID: webSocket.connectionID,
      wsHttpEndpoint: webSocket.endpoint,
    };

    const { data, error } = await postProjectExistingMotion(projectID, payload);

    if (error) {
      await handleGenericErrors(error);
      state.motion.addExistingMotionSubmitting = false;
      return { success: false, error: error?.message };
    } else if (data) {
      state.currentProject.data = data;
      reloadCurrentProjectGenerationHistory();
      state.motion.addExistingMotionSubmitting = false;
      return { success: true };
    }
  } catch (error) {
    state.motion.addExistingMotionSubmitting = false;
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }

  state.motion.addExistingMotionSubmitting = false;
  return { success: false, error: 'Unknown error' };
}

export async function addWebSocketToPendingJob(jobID: string) {
  if (!webSocket || !webSocket.connectionID || !webSocket.endpoint) {
    console.error('Websockets are not connected.');
    return;
  }

  const { data, error } = await postWsAndFetchJob(jobID, {
    wsConnectionID: webSocket.connectionID,
    wsHttpEndpoint: webSocket.endpoint,
  });

  if (error) {
    console.error('Error adding web socket to pending job:', error);
    return;
  }
}

export async function checkExportCost(format: EXPORT_TYPES, software?: string) {
  if (state.currentProject.status !== 'ready') return;

  const projectID = state.currentProject.data.projectID;
  const characters = state.currentProject.data.characters;
  const objectIds = Object.keys(characters);
  const objectID = objectIds[0]; // We don't support more than one character just yet

  if (!objectID) {
    console.error('No character found in project');
    return;
  }

  // Convert studio EXPORT_TYPES to dynamo-daos EXPORT_TYPES
  // PNG and MP4 are not valid for export cost, so we skip those
  const validFormats = [
    EXPORT_TYPES.BVH,
    EXPORT_TYPES.FBX_BLENDER,
    EXPORT_TYPES.FBX_MAYA,
    EXPORT_TYPES.FBX_UNREAL,
    EXPORT_TYPES.GLB,
    EXPORT_TYPES.MA,
    EXPORT_TYPES.MB,
  ];

  if (!validFormats.includes(format)) {
    console.error('Invalid export format for cost calculation:', format);
    return;
  }

  const { data, error } = await getExportCost(projectID, {
    format: format as unknown as DYNAMO_EXPORT_TYPES,
    objectID,
    software,
  });

  if (error) {
    console.error('Error fetching export cost:', error);
    return;
  }

  return data;
}

export async function deleteJobFromBackend(jobID: string) {
  console.log('deleting job', jobID);
  const { error } = await deleteJob(jobID);
  if (error) {
    await handleGenericErrors(error);
  } else {
    noticesStore.success('Job deleted!');

    if (state.currentProject.data) {
      state.currentProject.data.pendingJobs =
        state.currentProject.data?.pendingJobs?.filter((job) => job !== jobID);
    }
  }
}

export async function changeCurrentCharacter(characterID: string) {
  const projectID = state.currentProject.data?.projectID;
  if (!projectID) {
    console.error('No project ID found');
    return;
  }
  const characterInstanceID = Object.keys(
    state.currentProject.data?.characters || {},
  )[0];
  if (!characterInstanceID) {
    console.error('No character instance ID found');
    return;
  }
  const { data, error } = await putProjectCharacter(projectID, {
    characterInstanceID,
    overrideCharacterID: characterID,
  });

  // @deprecated - sceneSettingsStore.characterSettings will be removed in the future
  sceneSettingsStore.updateCharacter(characterID);

  if (error) {
    await handleGenericErrors(error);
  } else if (data) {
    state.currentProject.data = data;
  }
}
// #endregion

// #region HELPER FUNCTIONS

export function getJobIDsFromProjectTimeline(projectRecord: Project) {
  const jobIDs: string[] = [];
  for (const character of Object.values(projectRecord.characters)) {
    for (const timelineItem of character.timeline) {
      jobIDs.push(timelineItem.jobID);
    }
  }
  return jobIDs;
}
// #endregion

// #region SUBSCRIPTIONS

/**
 * Whenever the current projects data object changes, we want to derive motion state from it.
 */
subscribe(state, (operations) => {
  for (const operation of operations) {
    const [op, path] = operation;

    // Update when current project changes
    if (op === 'set' && path.includes('currentProject')) {
      deriveMotionStateFromCurrentProject();
    }
  }
});

async function deriveMotionStateFromCurrentProject() {
  if (state.currentProject.status !== 'ready') return;
  if (state.currentProject.data.type !== ProjectType.MOTION) return;

  const project = state.currentProject.data;

  const objectIds = Object.keys(project.characters);
  const objectID = objectIds[0]; // We don't support more than one character just yet

  const bvhURLs = project.characters[objectID].timeline
    .map((item) => [item.bvhURL, item.stitchBvhURL].filter(notEmpty))
    .flat();
  const rotationOffsets = project.characters[objectID].rotationOffsets;

  if (bvhURLs.length === 0) {
    state.motion.combinedBvhFile = null;
    state.motion.timelineData = null;
    return;
  }

  const bvhFiles = await loadBVHFiles(bvhURLs, rotationOffsets);
  state.motion.combinedBvhFile = mergeBVHFiles(bvhFiles);

  const groupedBVHFiles = chunkArray(bvhFiles, 2);
  const items: TimelineItem[] = [];
  let cumulativeStartValue = 0;

  const timelineItems = project.characters[objectID].timeline;

  for (const [index, item] of timelineItems.entries()) {
    const [bvhFile, stitchBVHFile] = groupedBVHFiles[index];
    let duration = bvhFile.clip.duration;
    if (stitchBVHFile) {
      duration += stitchBVHFile.clip.duration;
    }

    items.push({
      id: crypto.randomUUID(),
      jobID: item.jobID,
      title: item.title,
      trimStart: item.trimStart,
      trimEnd: item.trimEnd,
      variationNumber: item.variationNumber,
      comicMp4URL: item.comicMp4URL,
      generationType: item.generationType,
      originalBvhURL: item.originalBvhURL,
      stitchStartDelay: item.stitchStartDelay,
      stitchEndDelay: item.stitchEndDelay,
      startValue: cumulativeStartValue,
      duration,
    });

    cumulativeStartValue += duration;
  }
  state.motion.timelineData = {
    [objectID]: items,
  };
}

// #endregion

// #region WEBSOCKET EVENTS

webSocketStore.on('wake', async () => {
  if (state.currentProject.data?.projectID) {
    await loadCurrentProject(state.currentProject.data.projectID);
    reloadCurrentProjectGenerationHistory();
  }
});

webSocketStore.on('TIMELINE_UPDATED', async (data) => {
  IS_DEV_ENV && console.log('Timeline updated', data);

  await loadCurrentProject(data.projectID);
  finishMediaProcessing('SUCCESS');
  await userStore.refetchUserInfo();

  if (state.currentProject.data?.type === ProjectType.POSE) {
    IS_DEV_ENV && console.log('Running fitViewPose');
    setTimeout(() => {
      posingStore.state.posingSystem.current?.fitViewPose(
        posingStore.state.posingSystem.current?.poser.rig.wpose,
      );

      setTimeout(async () => {
        if (state.currentProject.data) {
          const canvasImageTaskURL =
            await posingStore.state.posingSystem.current?.canvasImageTask();
          const canvasBase64 = canvasImageTaskURL?.replace(
            /^data:image\/\w+;base64,/,
            '',
          );
          posingStore.setLastCanvasBase64(canvasBase64 || '');
          await posingStore.updateCurrentProjectPose(
            state.currentProject.data,
            canvasBase64 || '',
          );
        }
      }, 2000);
    }, 1000);
  }
});

webSocketStore.on('PROCESSING_VIDEO', async (data) => {
  // Set the media upload status to processing (i.e. not in queue anymore)
  if (state.mediaUpload) {
    state.mediaUpload.status = 'PROCESSING';
  }
  // refetch currentProject
  if (state.currentProject.data?.projectID) {
    await loadCurrentProject(state.currentProject.data.projectID);
    reloadCurrentProjectGenerationHistory();
  }
});

webSocketStore.on('VIDEO_FLAGGED', async () => {
  if (state.mediaUpload) {
    state.mediaUpload.status = 'ERROR';
  }
  navigationStore.setModal(StudioModalName.FLAGGED_VIDEO);
});

webSocketStore.on('FAILED', () => {
  finishMediaProcessing('ERROR');
});

webSocketStore.on('GENERATING_PK', async () => {
  // refetch currentProject
  if (state.currentProject.data?.projectID) {
    await loadCurrentProject(state.currentProject.data.projectID);
    reloadCurrentProjectGenerationHistory();
  }
});

export async function setFavorite(
  jobID: string,
  variationNumber: number,
  desiredStatus: boolean,
) {
  if (desiredStatus) {
    const { data, error } = await postFavorite(jobID, variationNumber);
    if (error) {
      handleGenericErrors(error);
      return { success: false };
    }
    return { success: true, data };
  } else {
    const { data, error } = await deleteFavorite(jobID, variationNumber);
    if (error) {
      handleGenericErrors(error);
      return { success: false };
    }
    return { success: true, data };
  }
}

webSocketStore.on('BVH_READY', async ({ jobID }) => {
  // refetch currentProject
  if (state.currentProject.data?.projectID) {
    await loadCurrentProject(state.currentProject.data.projectID);
    reloadCurrentProjectGenerationHistory();

    // Open edit motion modal if the job is a motion from text
    const objectID = Object.keys(state.currentProject.data?.characters)[0];
    const timeline = state.currentProject.data?.characters[objectID].timeline;
    const timelineItem = timeline?.find((item) => item.jobID === jobID);
    if (timelineItem?.generationType === GenerationType.MOTION_FROM_TEXT) {
      navigationStore.setModal(StudioModalName.EDIT_MOTION, {
        jobID,
        objectID,
      });
    }
  }
});

webSocketStore.on('EXPORT_READY', (data) => {
  if (!data.projectID) return;

  if (state.export.status === 'pending' && state.export.data?.settings) {
    const { format, includeMesh } = state.export.data.settings;
    let meshStr = '';
    // BVH is always meshless, .MA and .MB are always with-mesh
    if (format === EXPORT_TYPES.BVH) {
      meshStr = '-meshless';
    } else if (format === EXPORT_TYPES.MA || format === EXPORT_TYPES.MB) {
      meshStr = '-with-mesh';
    } else {
      meshStr = includeMesh ? '-with-mesh' : '-meshless';
    }
    const key = `${format?.toLowerCase()}${meshStr}`;
    const downloadURL = data.downloadURLs[key];
    state.export = {
      status: 'ready',
      data: {
        ...state.export.data,
        estimatedFinishTime: new Date().toISOString(),
        path: downloadURL,
      },
    };
  }
  // Refetch userInfo to keep the amount of spins up to date
  userStore.refetchUserInfo();
});
// #endregion
